import React, { useCallback, useState } from 'react';
import { AlertCircle, Shield } from 'lucide-react';
import { fetchUsers } from '../features/admin/adminReviewApi';
import { setAdminReviewToken } from '../shared/adminReviewAuth';

export interface AdminGatePageProps {
  onAuthenticated?: () => void;
}

/** Admin token gate — used by ProtectedRoute before AdminReviewPage renders. */
const AdminGatePage: React.FC<AdminGatePageProps> = ({ onAuthenticated }) => {
  const [tokenInput, setTokenInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onLogin = useCallback(async () => {
    const t = tokenInput.trim();
    if (!t) return;
    setError(null);
    setBusy(true);
    try {
      await fetchUsers(t, { limit: 1 });
      setAdminReviewToken(t);
      onAuthenticated?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Authentication failed';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }, [tokenInput, onAuthenticated]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Admin Review</h1>
          <p className="text-sm text-gray-500 text-center">
            Enter your admin token to access user data and session history.
          </p>
        </div>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label htmlFor="admin-token" className="block text-sm font-medium text-gray-700 mb-1">
              Admin Token
            </label>
            <input
              id="admin-token"
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && onLogin()}
              placeholder="Enter X-Admin-Token"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>
          <button
            type="button"
            onClick={onLogin}
            disabled={!tokenInput.trim() || busy}
            className="w-full py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? 'Signing in…' : 'Sign In'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminGatePage;
